import { useState } from 'react';
import { subscriptionGateway } from '../index';
import type { BillingStatusResponse } from '../billingTypes';
import type { CompanySubscriptionResponse } from '../types';
import { useBilling } from './BillingContext';
import { billingErrorMessage, isPermissionDenied } from './billingErrors';
import { HOSTED_REDIRECT_ERROR, useHostedRedirect } from './hostedRedirect';
import { paymentMethodLabel } from './money';

/**
 * Payment method summary ("Visa ending 4242") and the "Update payment method" action. Updating never happens in EPN:
 * the server opens a provider customer-portal session and the browser is sent there. Only non-sensitive text the
 * server supplied (brand, last four) is ever shown.
 */
export function PaymentMethodSection({
  subscription, status,
}: {
  subscription: CompanySubscriptionResponse | null;
  status: BillingStatusResponse | null;
}) {
  const { readOnly, markReadOnly, providerAvailable } = useBilling();
  const redirect = useHostedRedirect();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const method = status?.paymentMethod ?? subscription?.paymentMethod ?? null;
  const failed = subscription?.effectiveState === 'PastDueGrace' || subscription?.effectiveState === 'PastDueRestricted';
  const canUpdate = method !== null || failed;

  async function update() {
    setBusy(true);
    setError(null);
    try {
      const session = await subscriptionGateway.createPortalSession();
      try {
        redirect(session.url);
      } catch {
        setError(HOSTED_REDIRECT_ERROR);
      }
    } catch (err) {
      if (isPermissionDenied(err)) markReadOnly();
      else setError(billingErrorMessage(err, 'Could not open the payment method page.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="billing-payment-heading" className="billing-section" data-testid="billing-payment-method">
      <h3 id="billing-payment-heading">Payment method</h3>
      <p><strong data-testid="payment-method-label">{paymentMethodLabel(method)}</strong></p>

      {failed && (
        <p role="status">
          Your last payment did not go through. Update your payment method and we will retry the payment automatically.
        </p>
      )}
      {!method && !failed && (
        <p className="muted">You will add a payment method when you subscribe. A free trial needs none.</p>
      )}

      {error && <p className="error" role="alert">{error}</p>}

      {canUpdate && !readOnly && (
        <>
          <button type="button" disabled={busy || !providerAvailable} onClick={() => void update()}>
            {busy ? 'Opening secure page…' : 'Update payment method'}
          </button>
          <p className="muted">
            You will go to a secure page to change your card or US bank account. This is for your Escochex software
            subscription only.
          </p>
        </>
      )}
      {canUpdate && readOnly && (
        <p className="muted">Only a user with billing-management permission can update the payment method.</p>
      )}
    </section>
  );
}
